import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useSelector } from "react-redux";

import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Divider,
  Input,
  Link,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from "@mui/joy";
import { useGig } from "../../redux/gig/gigHooks";
import {
  useCreateGigMutation,
  useDeleteGigMutation,
  useUpdateGigMutation,
} from "../../redux/gig/gigEndpoints";
import {
  formOpenSelector,
  selectedGigIDsSelector,
  setFormOpen,
  setSelectedGigIDs,
  setTmpGig,
  tmpGigSelector,
} from "../../reducers/rhapsody";
import moment from "moment-timezone";
import { Gig_Entity } from "../../entities/gig";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useProjects } from "../../redux/project/projectHooks";
import { typeOptions } from "../../constants";
import { Cover } from "../cover/cover";
import { useVenues } from "../../redux/venue/venueHooks";
import { useContractors } from "../../redux/contractor/contractorHooks";
import { GigPiece_Entity } from "../../entities/gigPiece";
import { useSectionRoles } from "../../redux/sectionRole/sectionRoleHooks";
import { useSections } from "../../redux/section/sectionHooks";
import { NerdCurrencyField } from "@nerdjs/nerd-ui";
import { CurrencyInput } from "../../entities/currencyInput/currencyInput";
import { DateTimePicker } from "../../entities/dateTimePicker/dateTimePicker";
import { useMyMember } from "../../redux/member/memberHooks";
import { Project } from "../../entities/project";
import { Section } from "../../entities/section";
import { SectionRole } from "../../entities/sectionRole";
import { useInstruments } from "../../redux/instrument/instrumentHooks";
import { InstrumentsPopover } from "../instrumentsPopover/instrumentsPopover";
import { PieceFinder } from "../pieceFinder/pieceFinder";
import { InstrumentsTooltip } from "../instrumentsPopover/instrumentsTooltip";
import {
  useLibrarianPiece,
  useLibrarianPieces,
} from "../../redux/librarianPiece/librarianPieceHooks";
import { RoleTooltip } from "../roleTooltip/roleTooltip";
import { SectionTooltip } from "../sectionTooltip/sectionTooltip";
import { usePro } from "../usePro/usePro";
import { useProjectIDs } from "../../routes/project";
import { FieldLabel, GroupCaption } from "../formKit/formKit";
import { EntityPicker } from "./EntityPicker";

/**
 *
 * @returns {ReactElement} FormGig page
 */
export function FormGig() {
  const gigIDs = useSelector(selectedGigIDsSelector);
  const projectIDs = useProjectIDs();
  const { gig } = useGig(gigIDs);
  const open = useSelector(formOpenSelector("gig"));
  const [_gig, setGig] = useState<Partial<Gig_Entity>>({});
  const [_pieces, setPieces] = useState<Partial<GigPiece_Entity>[]>([
    { instrumentIDs: "[]", title: "none" },
  ]);
  const tmpGig = useSelector(tmpGigSelector);
  const { projects } = useProjects();
  const { venues } = useVenues();
  const { contractors } = useContractors();
  const { sectionRoles, sectionRolesMap } = useSectionRoles();
  const { sections, sectionsMap } = useSections();
  const { instrumentsMap } = useInstruments();
  const pro = usePro();
  const librarianPieceIDs = _pieces.reduce((a, v) => {
    a.push(v.librarianPieceID);
    return a;
  }, []);
  const { librarianPiecesMap, librarianPieces } = useLibrarianPieces(
    {
      filters: JSON.stringify({
        name: "pieces.id",
        comparison: "in",
        value: librarianPieceIDs,
      }),
    },
    { skip: false }
  );

  useEffect(() => {
    setGig((g) => ({
      ...g,
      ...(projectIDs ? { projectID: projectIDs?.id } : {}),
      // New services carry an explicit zone so the card and the form agree; an
      // existing gig overwrites this when it loads in the effect below.
      timezone: g.timezone || moment.tz.guess(),
    }));
  }, []);

  const [createGig] = useCreateGigMutation();
  const [updateGig] = useUpdateGigMutation();
  const [deleteGig] = useDeleteGigMutation();

  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLInputElement) | null
  >(null);

  const selectedVenue = venues.find((v) => v.id === _gig.venueID);

  // Zone the times are shown/stored in: the venue's when set, otherwise the
  // gig's own timezone, falling back to the browser guess for brand-new
  // services. Persisted on save so venue-less services stop drifting to UTC.
  const browserTz = moment.tz.guess();
  const effectiveTz = selectedVenue?.tzName || _gig.timezone || browserTz;

  // Re-anchor helper: keep the wall clock the user sees and re-interpret it in
  // a new zone (same behaviour as changing the venue), then store that zone.
  const handleTimezoneChange = (newTz: string) => {
    const prevTz = _gig.timezone || browserTz;
    const reanchor = (utc?: string) => {
      if (!utc) return utc;
      const wall = moment.tz(utc, prevTz).format("YYYY-MM-DDTHH:mm:ss");
      return moment.tz(wall, newTz).utc().format();
    };
    setGig((g) => ({
      ...g,
      timezone: newTz,
      dateFromUTC: reanchor(g.dateFromUTC),
      dateToUTC: reanchor(g.dateToUTC),
    }));
  };

  // Track the tz the stored instant was last entered/displayed in, so that
  // when the venue (and thus tz) changes we can re-anchor the SAME displayed
  // wall clock to the new zone instead of letting the instant drift. The
  // picker shows times in the browser zone until a venue is chosen, so that
  // is the effective "previous" zone for the first venue selection.
  const prevTzRef = useRef<string | undefined>(undefined);
  // Distinguishes "a gig was just loaded for editing" (don't re-anchor — the
  // stored instant is authoritative) from "the user changed the venue".
  const loadedExistingRef = useRef(false);

  // When the venue's tz changes after a time was already picked, keep the
  // displayed wall clock and re-interpret it in the new zone. e.g. 7:00 PM
  // entered (browser/PST) becomes the UTC instant for 7:00 PM EST once the
  // venue moves to New York — the user still sees 7:00 PM, but the stored UTC
  // is corrected.
  useEffect(() => {
    const newTz = selectedVenue?.tzName;
    // Until a venue is picked the picker uses the gig's own zone (or the
    // browser guess); treat that as the prior zone so the first venue
    // selection corrects the instant.
    const prevTz = prevTzRef.current ?? _gig.timezone ?? moment.tz.guess();
    prevTzRef.current = newTz;

    // Don't touch the instant we just loaded from an existing gig.
    if (loadedExistingRef.current) {
      loadedExistingRef.current = false;
      return;
    }

    // Nothing to re-anchor without both a real zone change and a stored time.
    if (!newTz || prevTz === newTz) return;
    if (!_gig.dateFromUTC && !_gig.dateToUTC) return;

    const reanchor = (utc?: string) => {
      if (!utc) return utc;
      // Wall clock as displayed under the previous zone...
      const wall = moment.tz(utc, prevTz).format("YYYY-MM-DDTHH:mm:ss");
      // ...re-interpreted in the new zone, keeping that wall clock.
      return moment.tz(wall, newTz).utc().format();
    };

    setGig((g) => ({
      ...g,
      dateFromUTC: reanchor(g.dateFromUTC),
      dateToUTC: reanchor(g.dateToUTC),
    }));
  }, [selectedVenue?.tzName]);

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  const openQuickCreate = (formID: "project" | "venue" | "contractor") => {
    dispatch(setFormOpen({ isOpen: true, formID }));
  };

  const local420Projects = projects.filter((p) => p.sourceID === 420);
  const local420Venues = venues.filter((p) => p.sourceID === 420);
  const local420Contractors = contractors.filter((p) => p.sourceID === 420);

  useEffect(() => {
    if (gig?.id) {
      // The loaded instant is authoritative; the venue-tz re-anchor effect
      // must skip the tz change that this load triggers.
      loadedExistingRef.current = true;
      setGig(gig);
      setPieces(gig.pieces);
    }
  }, [gig]);

  useEffect(() => {
    // tmpGig is the cross-form channel: it seeds the date from the calendar and
    // carries back records just created via the inline "+ New …" pickers.
    const patch: Partial<Gig_Entity> = {};
    if (tmpGig.dateFromUTC) {
      patch.dateFromUTC = tmpGig.dateFromUTC;
      patch.dateToUTC = tmpGig.dateToUTC;
    }
    if (tmpGig.projectID) patch.projectID = tmpGig.projectID;
    if (tmpGig.venueID) patch.venueID = tmpGig.venueID;
    if (tmpGig.contractorID) patch.contractorID = tmpGig.contractorID;
    if (Object.keys(patch).length) {
      setGig((g) => ({ ...g, ...patch }));
      dispatch(setTmpGig({}));
    }
  }, [tmpGig]);

  const onClose = () => {
    setGig({});
    dispatch(setFormOpen({ isOpen: false, formID: "gig" }));
    dispatch(setSelectedGigIDs());
  };

  const save = () => {
    if (!pro)
      return dispatch(setFormOpen({ isOpen: true, formID: "upgradeOpen" }));
    // Always persist a concrete zone so the card and the form never disagree.
    const body = { ..._gig, timezone: effectiveTz };
    if (gigIDs && _gig) {
      updateGig({ id: gigIDs.id, body });
    } else if (_gig) {
      createGig({ ...body, pieces: _pieces });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Project Piece. Chairs linked to this Project
          Piece will be deleted as well. .
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (gigIDs) deleteGig(gigIDs.id);
    onClose();
  };

  console.log(_gig);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "20px" } }}
    >
      <DialogTitle>
        <Box>
          <Typography
            level="h5"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {gigIDs ? "Edit" : "New"} Service
          </Typography>
          {!gigIDs ? (
            <Typography
              level="body2"
              sx={{ color: "var(--mui-palette-text-secondary)" }}
            >
              A name and a date is all it takes — everything else can wait.
            </Typography>
          ) : (
            []
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 500,
          maxWidth: "100%",
        }}
      >
        {!pro ? (
          <Alert
            color="info"
            startDecorator={<i className="fa-regular fa-circle-info"></i>}
          >
            Add Services offered outside of Rhapsody allowing you to manage all
            your gigs seamlessly in one platform.
          </Alert>
        ) : (
          []
        )}
        <GroupCaption>The basics</GroupCaption>
        <Sheet
          variant="soft"
          sx={{ borderRadius: "14px", overflow: "hidden", flexShrink: 0 }}
        >
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            autoFocus
            placeholder="e.g. Evening Rehearsal"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-signature"
                color="#007AFF"
                label="Name"
              />
            }
            value={_gig.title}
            onChange={(e) => setGig((v) => ({ ...v, title: e.target.value }))}
            type="text"
          />
          <Divider />
          <EntityPicker
            label="Project"
            icon="fa-solid fa-folder-open"
            color="#5856D6"
            options={local420Projects}
            valueId={_gig.projectID}
            onSelect={(id) => setGig((v) => ({ ...v, projectID: id }))}
            onCreateNew={() => openQuickCreate("project")}
          />
          <Divider />
          <Autocomplete
            sx={{ background: "transparent" }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-list-music"
                color="#AF52DE"
                label="Type"
              />
            }
            options={typeOptions}
            key={_gig.sessionTypeID}
            value={
              (typeOptions?.find((p) => p.id === _gig.sessionTypeID) ??
                null) as any
            }
            getOptionLabel={(option) => (option as any)?.title ?? ""}
            slotProps={{
              input: { style: { textAlign: "right" } },
              listbox: {
                sx: { zIndex: 9999 },
              },
            }}
            onChange={(e, value) => {
              setGig((v) => ({
                ...v,
                sessionTypeID: (value as any)?.id,
              }));
            }}
          />
          <Divider />
          <EntityPicker
            label="Venue"
            icon="fa-solid fa-location-dot"
            color="#FF9500"
            options={local420Venues}
            valueId={_gig.venueID}
            onSelect={(id) => setGig((v) => ({ ...v, venueID: id }))}
            onCreateNew={() => openQuickCreate("venue")}
          />
          <Divider />
          <EntityPicker
            label="Hired by"
            createLabel="Organization"
            icon="fa-solid fa-building"
            color="#5AC8FA"
            options={local420Contractors}
            valueId={_gig.contractorID}
            onSelect={(id) => setGig((v) => ({ ...v, contractorID: id }))}
            onCreateNew={() => openQuickCreate("contractor")}
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel icon="fa-solid fa-link" color="#8E8E93" label="URL" />
            }
            value={_gig.url}
            onChange={(e) => setGig((v) => ({ ...v, url: e.target.value }))}
            type="text"
          />
          <Divider />
          <Textarea
            sx={{ background: "transparent" }}
            minRows={3}
            variant="plain"
            placeholder="Anything worth remembering — parking, entrance, who to ask for…"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-note-sticky"
                color="#FF2D55"
                label="Notes"
              />
            }
            value={_gig.memo}
            onChange={(e) => setGig((v) => ({ ...v, memo: e.target.value }))}
            // type="text"
          />
        </Sheet>
        <GroupCaption>When</GroupCaption>
        <Sheet
          variant="soft"
          sx={{ borderRadius: "14px", p: 1, flexShrink: 0 }}
        >
          <FieldLabel
            icon="fa-solid fa-calendar-days"
            color="#FF3B30"
            label="Date & Time"
          />
          <DateTimePicker
            key={`${_gig.dateFromUTC}-${effectiveTz}`}
            dateFrom={_gig.dateFromUTC}
            dateTo={_gig.dateToUTC}
            tzName={effectiveTz}
            locked={Boolean(selectedVenue?.tzName)}
            onChange={(from, to) => {
              setGig((v) => ({ ...v, dateFromUTC: from, dateToUTC: to }));
            }}
          />
          {!selectedVenue?.tzName ? (
            <>
              <Divider sx={{ my: 1 }} />
              <Autocomplete
                sx={{ background: "transparent" }}
                variant="plain"
                disableClearable
                options={moment.tz.names()}
                value={effectiveTz as any}
                getOptionLabel={(option) => option as string}
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-earth-americas"
                    color="#5AC8FA"
                    label="Timezone"
                  />
                }
                slotProps={{
                  input: { style: { textAlign: "right" } },
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(e, tz) => {
                  if (tz) handleTimezoneChange(tz as unknown as string);
                }}
              />
            </>
          ) : (
            []
          )}
        </Sheet>
        <GroupCaption optional>Your part</GroupCaption>
        {_pieces.length === 1 ? (
          <Sheet
            variant="soft"
            sx={{ borderRadius: "14px", overflow: "hidden", flexShrink: 0 }}
          >
            {/* <Select
              sx={{ background: "transparent" }}
              variant="plain"
              startDecorator="Position: "
              value={_pieces[0].sectionOrder}
              slotProps={{
                button: { sx: { justifyContent: "end" } },
                listbox: {
                  sx: { zIndex: 9999 },
                },
              }}
              onChange={(e, value) => {
                const p: Partial<GigPiece_Entity[]> = JSON.parse(
                  JSON.stringify(_pieces)
                );
                p[0].sectionOrder = value;
                setPieces(p);
              }}
            >
              {Array.from({ length: 20 }, (_, i) => i + 1).map((p) => (
                <Option value={p} key={p} sx={{ gap: 1 }}>
                  {p}
                </Option>
              ))}
            </Select> */}
            {/* <Divider /> */}
            <Autocomplete
              sx={{ background: "transparent" }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-chair"
                  color="#34C759"
                  label="Role"
                />
              }
              value={
                (sectionRoles?.find((p) => p.id === _pieces[0].sectionRoleID) ??
                  null) as any
              }
              options={sectionRoles}
              getOptionLabel={(option) =>
                (option as unknown as SectionRole)?.name ?? ""
              }
              slotProps={{
                input: { style: { textAlign: "right" } },
                listbox: {
                  sx: { zIndex: 9999 },
                },
              }}
              onChange={(e, value) => {
                const p: Partial<GigPiece_Entity[]> = JSON.parse(
                  JSON.stringify(_pieces)
                );
                p[0].sectionRoleID = (value as unknown as Section)?.id;
                setPieces(p);
              }}
            />
            <Divider />
            <Input
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ background: "transparent" }}
              variant="plain"
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              endDecorator={
                <button
                  aria-label="Open"
                  title="Open"
                  type="button"
                  className="MuiAutocomplete-popupIndicator css-zhgfkv-JoyAutocomplete-popupIndicator"
                >
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    data-testid="ArrowDropDownIcon"
                    className="MuiSvgIcon-root MuiSvgIcon-colorInherit MuiSvgIcon-fontSizeXl css-hl2kr4-JoySvgIcon-root"
                  >
                    <path d="M7 10l5 5 5-5z"></path>
                  </svg>
                </button>
              }
              value={JSON.parse(_pieces[0].instrumentIDs)
                ?.reduce((a, v) => {
                  a.push(instrumentsMap[v].name);
                  return a;
                }, [])
                .join(",")}
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-guitar"
                  color="#FF9500"
                  label="Instrument(s)"
                />
              }
              readOnly
            />
            <InstrumentsPopover
              // autoAdd
              myInstruments
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              onSelect={(e) => {
                const p: Partial<GigPiece_Entity[]> = JSON.parse(
                  JSON.stringify(_pieces)
                );
                p[0].instrumentIDs = JSON.stringify(e);
                setPieces(p);
              }}
            />
            <Divider />
            <Autocomplete
              sx={{ background: "transparent" }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-users"
                  color="#5856D6"
                  label="Section"
                />
              }
              options={sections}
              value={
                (sections?.find((s) => s.id === _pieces[0].sectionID) ??
                  null) as any
              }
              getOptionLabel={(option) =>
                (option as unknown as Project)?.name ?? ""
              }
              slotProps={{
                input: { style: { textAlign: "right" } },
                listbox: {
                  sx: { zIndex: 9999 },
                },
              }}
              onChange={(e, value) => {
                const p: Partial<GigPiece_Entity[]> = JSON.parse(
                  JSON.stringify(_pieces)
                );
                p[0].sectionID = (value as unknown as Section)?.id;
                setPieces(p);
              }}
            />
            <Divider />
            <PieceFinder
              onSelect={(e) => {
                const _piece: Partial<GigPiece_Entity> = {
                  title: e.title,
                  composer: e.composer,
                  librarianPieceID: e.id,
                  instrumentIDs: _pieces[0].instrumentIDs,
                  sectionOrder: _pieces[0].sectionOrder,
                  sectionRoleID: _pieces[0].sectionRoleID,
                };
                if (_pieces[0].title === "none") {
                  setPieces((e) => [_piece]);
                } else {
                  setPieces((e) => [...e, _piece]);
                }
              }}
            >
              <Input
                sx={{ background: "transparent" }}
                variant="plain"
                slotProps={{
                  input: { style: { textAlign: "right" } },
                }}
                endDecorator={
                  <button
                    aria-label="Open"
                    title="Open"
                    type="button"
                    className="MuiAutocomplete-popupIndicator css-zhgfkv-JoyAutocomplete-popupIndicator"
                  >
                    <svg
                      focusable="false"
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      data-testid="ArrowDropDownIcon"
                      className="MuiSvgIcon-root MuiSvgIcon-colorInherit MuiSvgIcon-fontSizeXl css-hl2kr4-JoySvgIcon-root"
                    >
                      <path d="M7 10l5 5 5-5z"></path>
                    </svg>
                  </button>
                }
                value={
                  _pieces[0].title !== "none"
                    ? `${_pieces[0].title}, ${_pieces[0].composer}`
                    : ""
                }
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-music"
                    color="#AF52DE"
                    label="Piece"
                  />
                }
                readOnly
              />
            </PieceFinder>
            {_pieces[0].title !== "none" ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "end",
                  alignItems: "end",
                }}
              >
                <Button
                  onClick={() => {
                    const _piece = _pieces[0];
                    _piece.librarianPieceID = undefined;
                    _piece.title = "none";
                    _piece.composer = "";
                    setPieces([_piece]);
                  }}
                  variant="plain"
                  color="danger"
                  size="sm"
                >
                  Clear Piece
                </Button>
                <PieceFinder
                  onSelect={(e) => {
                    const _piece: Partial<GigPiece_Entity> = {
                      title: e.title,
                      composer: e.composer,
                      librarianPieceID: e.id,
                      instrumentIDs: _pieces[0].instrumentIDs,
                      sectionOrder: _pieces[0].sectionOrder,
                      sectionRoleID: _pieces[0].sectionRoleID,
                      sectionID: _pieces[0].sectionID,
                    };
                    setPieces((e) => [...e, _piece]);
                  }}
                >
                  <Button variant="plain" color="primary" size="sm">
                    + Add Piece
                  </Button>
                </PieceFinder>
              </Box>
            ) : (
              []
            )}
          </Sheet>
        ) : (
          <Sheet
            variant="soft"
            sx={{ borderRadius: "14px", overflow: "hidden", flexShrink: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
              }}
            >
              <FieldLabel
                icon="fa-solid fa-music"
                color="#AF52DE"
                label="Repertoire"
              />
              <Typography level="body2" sx={{ color: "#6C6C6C" }}>
                {_pieces.length} Piece{_pieces.length > 1 ? "s" : ""}
              </Typography>
            </Box>
            {_pieces.map((p, i) => (
              <Box
                key={p.librarianPieceID}
                sx={{ p: 1, display: "flex", alignItems: "center" }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography>
                    {p.title}, {p.composer}
                  </Typography>
                  <Typography level="body2">
                    Section:{" "}
                    <SectionTooltip
                      onSelect={(e) => {
                        const _p = [..._pieces];
                        _p[i].sectionID = e;
                        setPieces(_p);
                      }}
                    >
                      <Typography
                        level="body3"
                        variant="outlined"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            background: "#2196f322",
                          },
                        }}
                      >
                        {sectionsMap[p.sectionID]?.name ?? "None"}
                      </Typography>
                    </SectionTooltip>{" "}
                    Role:{" "}
                    <RoleTooltip
                      onSelect={(e) => {
                        const _p = [..._pieces];
                        _p[i].sectionRoleID = e;
                        setPieces(_p);
                      }}
                    >
                      <Typography
                        level="body3"
                        variant="outlined"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            background: "#2196f322",
                          },
                        }}
                      >
                        {sectionRolesMap[p.sectionRoleID]?.name ?? "None"}
                      </Typography>
                    </RoleTooltip>
                    <br />
                    Instrument(s):{" "}
                    <InstrumentsTooltip
                      myInstruments
                      onSelect={(e) => {
                        const _p = [..._pieces];
                        _p[i].instrumentIDs = JSON.stringify(e);
                        setPieces(_p);
                      }}
                    >
                      <Typography
                        level="body3"
                        variant="outlined"
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            background: "#2196f322",
                          },
                        }}
                      >
                        {JSON.parse(p.instrumentIDs)
                          ?.reduce((a, v) => {
                            a.push(instrumentsMap[v].name);
                            return a;
                          }, [])
                          .join(",")}
                        {JSON.parse(p.instrumentIDs).length === 0 ? "None" : ""}
                      </Typography>
                    </InstrumentsTooltip>
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Link
                      onClick={() => {
                        const _p = [..._pieces];
                        _p.splice(i, 1);
                        setPieces(_p);
                      }}
                      color="danger"
                      level="body4"
                    >
                      Delete
                    </Link>
                    {librarianPiecesMap[_pieces[i].librarianPieceID]?.link ? (
                      <Link
                        onClick={() => {
                          window.open(
                            librarianPiecesMap[_pieces[i].librarianPieceID]
                              ?.link,
                            "_blank"
                          );
                        }}
                        level="body4"
                        endDecorator={
                          <i className="fa-solid fa-arrow-up-right"></i>
                        }
                      >
                        IMSLP
                      </Link>
                    ) : (
                      []
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                alignItems: "end",
              }}
            >
              <PieceFinder
                onSelect={(e) => {
                  const _piece: Partial<GigPiece_Entity> = {
                    title: e.title,
                    composer: e.composer,
                    librarianPieceID: e.id,
                    instrumentIDs: _pieces[0].instrumentIDs,
                    sectionOrder: _pieces[0].sectionOrder,
                    sectionRoleID: _pieces[0].sectionRoleID,
                    sectionID: _pieces[0].sectionID,
                  };
                  setPieces((e) => [...e, _piece]);
                }}
              >
                <Button variant="plain" color="primary" size="sm">
                  + Add Piece
                </Button>
              </PieceFinder>
            </Box>
          </Sheet>
        )}
        <GroupCaption optional>Good to know</GroupCaption>
        <Sheet
          variant="soft"
          sx={{ borderRadius: "14px", overflow: "hidden", flexShrink: 0 }}
        >
          <CurrencyInput
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-dollar-sign"
                color="#34C759"
                label="Wage"
              />
            }
            value={_gig.wages}
            onChange={(e) =>
              setGig((v) => ({ ...v, wages: parseFloat(e.target.value) }))
            }
            type="text"
          />
          <Divider />
          <Textarea
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-user-tie"
                color="#8E8E93"
                label="Dress Code"
              />
            }
            placeholder="e.g. All black, concert attire…"
            sx={{ background: "transparent" }}
            minRows={3}
            variant="plain"
            value={_gig.dressCode}
            onChange={(e) =>
              setGig((v) => ({ ...v, dressCode: e.target.value }))
            }
            // type="text"
          />
        </Sheet>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {gigIDs ? (
          <Button
            variant="soft"
            onClick={askDelete}
            color="danger"
            sx={{ borderRadius: "999px", fontWeight: 600 }}
          >
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={onClose}
          color="neutral"
          variant="plain"
          sx={{ borderRadius: "999px", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          sx={{ borderRadius: "999px", fontWeight: 600, px: 3 }}
          endDecorator={
            !pro ? (
              <Typography
                sx={{ color: "white" }}
                variant="outlined"
                color="neutral"
                level="body5"
              >
                PRO
              </Typography>
            ) : undefined
          }
          onClick={save}
          color="info"
        >
          {gigIDs ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
